import React, { useRef, useCallback, useEffect, useState, useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import { AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import { useRecoilValue } from 'recoil';
import {
  AuditLog,
  card,
  CircleIcon,
  FlexColumn,
  Header2,
  InfiniteList,
  Search as BaseSearch
} from '@ubeya/shared-web/components';
import usePortal from '@ubeya/shared-web/hooks/usePortal';
import { maxMedia } from '@ubeya/shared-web/style';
import useAuditLogs from '@ubeya/shared/hooks/account/useAuditLogs';
import { auditLogSidebarRef } from '@ubeya/shared/atoms/shared';
import { exportFormattedAuditLogs, generateAuditLogBody } from '@ubeya/shared/utils/audit-logs';
import { ReactComponent as ExcelIcon } from '@ubeya/shared-web/assets/file-excel.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import { ReactComponent as CloseIcon } from '@ubeya/shared-web/assets/close-big.svg';
import privileges from '@ubeya/shared-web/privileges';
import EmptyState from './EmptyState';

const MAX_EXPORT_PAGE_SIZE = 1000;

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background: rgba(19, 21, 41, 0.3);
  z-index: 100;
`;

const jumpIn = keyframes`
  0%{transform: translateX(100%);}
  100%{transform: translateX(0);}
`;

const Container = styled(FlexColumn)`
  ${card};
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  max-width: min(420px, 100%);
  border-radius: 8px 0 0 8px;
  animation: ${jumpIn} 300ms;
  overflow: auto;
  z-index: 110;
  ${maxMedia.mobile`overflow-x: hidden`};
`;

const HeaderRow = styled.div`
  display: flex;
`;

const Header = styled(Header2)`
  font-weight: 500;
  padding: 10px 20px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  margin: 0 0 19px;
  border: solid 1px var(--cloudy-blue);
  background-color: ${({ theme }) => theme.colors.grey3};
`;

const ActionsContainer = styled.div`
  margin-right: 5%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
`;

const RowContainer = styled.div`
  padding: 0 20px;
`;

const Search = styled(BaseSearch)`
  max-width: 100%;
`;

const generateParsedAuditLogData = (item) => {
  const { type, data, ...rest } = item || {};
  return { type, data: JSON.parse(data), ...rest };
};

const AuditLogSidebar = ({ showExport = false, onClose }) => {
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);

  const ref = useRef();
  const { t, dateFormat, shortDateMonthFormat, timeFormat, currency } = useContext(ConfigContext);

  const myPrivileges = useContext(PrivilegesContext);
  const hasSensitiveDataAccess = !!myPrivileges[privileges.SENSITIVE_DATA_ACCESS];
  const { Portal } = usePortal({ enabled: true, portalId: 'modal' });
  const { payload } = useRecoilValue(auditLogSidebarRef);
  const { isFetching, isLoading, data: dataInput, canFetchMore, fetchMore, exportAuditLogs } = useAuditLogs({
    projectIds: payload?.projectIds
  });

  const _cache = useRef(new CellMeasurerCache({ defaultHeight: 350, fixedWidth: true }));

  const showNoData = data.length === 0;
  const list = useRef();

  useEffect(() => {
    const formattedDataInput = dataInput.map((dataRow) => {
      const formattedData = generateParsedAuditLogData(dataRow);

      const { text, values } = generateAuditLogBody({
        t,
        type: dataRow.type,
        data: formattedData,
        timeFormat,
        shortDateMonthFormat,
        currency,
        hasSensitiveDataAccess
      });

      const translatedValue = Object.keys(values).length === 0 ? text : t(text, values);
      const translatedAuditLog = translatedValue?.replace(/<[^>]*>/g, '') || '';

      return { ...dataRow, translatedAuditLog };
    });

    if (search === '') {
      setData(formattedDataInput);
    } else {
      setData(
        formattedDataInput.filter(
          (dataRow) =>
            dataRow.data.toLowerCase().includes(search.toLowerCase()) ||
            dataRow.translatedAuditLog.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [dataInput, search, shortDateMonthFormat, t, timeFormat, currency, hasSensitiveDataAccess]);

  useEffect(() => {
    _cache.current.clearAll();
    list.current?.recomputeRowHeights?.();
  }, [data]);

  const rowRenderer = useCallback(
    ({ index, key, parent, style }) => {
      /* since search is frontend, if ther is more to fetch but current data is empty (search term was not found),
         there will be a weird loader */
      if (!data?.[index] && (!!search || !canFetchMore)) {
        return null;
      }

      const formattedData = data[index] ? generateParsedAuditLogData(data[index]) : [];

      return (
        <CellMeasurer cache={_cache.current} columnIndex={0} key={key} parent={parent} rowIndex={index}>
          {({ registerChild }) => (
            <RowContainer style={style} ref={registerChild}>
              <Divider />
              {data?.[index] ? (
                <AuditLog type={data[index]?.type} data={formattedData} />
              ) : (
                <>
                  <EmptyState showLoader />
                </>
              )}
            </RowContainer>
          )}
        </CellMeasurer>
      );
    },
    [data, search, canFetchMore]
  );

  const onExportAuditLogs = useCallback(async () => {
    const exportData = await exportAuditLogs({ pageSize: MAX_EXPORT_PAGE_SIZE });

    exportFormattedAuditLogs({
      t,
      auditLogs: exportData?.data || [],
      dateFormat,
      shortDateMonthFormat,
      timeFormat,
      currency,
      hasSensitiveDataAccess
    });
  }, [dateFormat, exportAuditLogs, shortDateMonthFormat, t, timeFormat, currency, hasSensitiveDataAccess]);

  return (
    <Portal>
      <Wrapper onClick={onClose}>
        <Container
          ref={ref}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}>
          <HeaderRow>
            <Header>{t('auditLogs')}</Header>

            <ActionsContainer>
              <Search value={search} onChange={setSearch} />
              {showExport && (
                <CircleIcon icon={ExcelIcon} tooltip={t('exportProjectRecentActions')} onClick={onExportAuditLogs} />
              )}
              <CircleIcon icon={CloseIcon} onClick={onClose} />
            </ActionsContainer>
          </HeaderRow>

          <AutoSizer>
            {({ width, height }) => (
              <InfiniteList
                rowCount={data.length}
                height={height}
                width={width}
                overscanRowCount={5}
                canFetchMore={canFetchMore}
                deferredMeasurementCache={_cache.current}
                rowHeight={_cache.current.rowHeight}
                fetchMore={fetchMore}
                rowRenderer={rowRenderer}
                innerRef={list}
              />
            )}
          </AutoSizer>
          {showNoData && <EmptyState showLoader={isFetching || isLoading} showNoData />}
        </Container>
      </Wrapper>
    </Portal>
  );
};

export default AuditLogSidebar;
